import React, {useCallback, useEffect, useMemo, useState} from 'react';

//CLAS
import Layout from '../../components/Layout';
import SidePanelContent from './SidePanelContent';
import MainContent from './MainContent';
import DashboardView from './DashboardView';

//UTILS
import {
  SIDEPANEL_CONTENT_ID,
  MAIN_CONTENT_ID,
  INITIAL_VIEWPORT,
  PNOA_HISTORICO_ID,
  PNOA_DEFAULT_YEAR,
  INITIAL_MAPSTYLE
} from '@/config';

import {PNOA_HISTORICO_BASEMAP} from '@/utils/pnoa';
import {useAsync} from 'react-use';
import {Style} from 'mapbox-gl';
import {Precinct} from '@/domain/entities/Precinct';
import {FilterGroup} from '@/domain/services/Clas';

import getDashboardDataUseCase from '@/domain/useCases/getDashboardDataUseCase';
import getFilteredPrecinctsUseCase from '@/domain/useCases/getFilteredPrecinctsUseCase';
import getProfileUseCase from '@/domain/useCases/getProfileUseCase';
import editPrecinctUseCase from '@/domain/useCases/editPrecinctUseCase';
import getManifestUseCase from '@/domain/useCases/getManifestUseCase';
import getUsersUseCase from '@/domain/useCases/getUsersUseCase';
import Typography from '@mui/material/Typography';

import {useTranslation} from 'react-i18next';
import getPrecinctByPointOfInterest from '@/domain/useCases/getPrecinctByPointOfInterest';
import getParcelByPointOfInterest from '@/domain/useCases/getParcelByPointOfInterest';

const Index = () => {

  const {t} = useTranslation();

  const [mapStyleId, setMapStyleId] = useState<string>(INITIAL_MAPSTYLE.id);
  const [PNOASelectedYear, setPNOASelectedYear] = useState<number>(PNOA_DEFAULT_YEAR);
  const [mapDetail, setMapDetail] = useState<Array<string>>([]);
  const [overlayIds, setOverlayIds] = useState<Array<string>>([]);
  const [viewport, setViewport] = useState(INITIAL_VIEWPORT);
  const [filterGroup, setFilterGroup] = useState<FilterGroup>({});
  const [pointOfInterest, setSelectedPointOfInterest] = useState<{lng: number, lat: number} | undefined>();
  const [refreshPrecinct, setRefreshPrecinct] = useState<boolean>(true); //Fuerza volver a pedir el recinto cuando se edita cultivo o uso
  const [year, setYear] = useState<number>();

  const fetchPrecinct = useCallback(
    () => getPrecinctByPointOfInterest(year, pointOfInterest),
    [year, pointOfInterest, refreshPrecinct]
  );

  const {value: manifest, loading: manifestLoading} = useAsync(() => getManifestUseCase());
  const {value: profile, loading: profileLoading} = useAsync(() => getProfileUseCase());
  const {value: otherUsers, loading: otherUsersLoading} = useAsync(() => getUsersUseCase());
  const {value: filteredPrecincts, loading: filteredPrecinctsLoading} = useAsync(async () => getFilteredPrecinctsUseCase(filterGroup, year), [filterGroup]);
  const {value: dashboardData, loading: dashboardLoading} = useAsync(() => getDashboardDataUseCase(filterGroup, year), [filterGroup]);
  const {value: selectedPrecinct, loading: selectedPrecinctLoading} = useAsync(fetchPrecinct, [fetchPrecinct]);
  const {value: selectedParcel, loading: selectedParcelLoading} = useAsync(() => getParcelByPointOfInterest(year, pointOfInterest), [pointOfInterest]);

  const initialSelectedYear = useMemo(
    () => [...(manifest?.availableYears ?? [])].sort((a, b) => b - a)[0],
    [manifest?.availableYears]
  );

  useEffect(() => {
    if (year === undefined && initialSelectedYear !== undefined) {
      setYear(initialSelectedYear);
    }
  }, [initialSelectedYear, year]);

  useEffect(() => {
    setFilterGroup({});
    setSelectedPointOfInterest(undefined);
  }, [year]);

  const handlePointSelected = async (pointOfInterest?: {lng: number, lat: number}) => setSelectedPointOfInterest(pointOfInterest);

  const handleYearChange = (year: number) => setYear(year);

  const handleInfoSave = async (value: Partial<Precinct>) => {
    if (!selectedPrecinct?.id) return;
    await editPrecinctUseCase({id: selectedPrecinct.id, ...value});
    setRefreshPrecinct(!refreshPrecinct);
  };

  //MAP
  const handleMapStyleChange = (mapStyleId: string) => setMapStyleId(mapStyleId);

  const mapStyle = useMemo<Style | string>(() => {
    if (mapStyleId !== PNOA_HISTORICO_ID) {
      return mapStyleId;
    }
    if (PNOASelectedYear) {
      return PNOA_HISTORICO_BASEMAP(PNOASelectedYear) as Style;
    }
    return mapStyleId;
  }, [mapStyleId, PNOASelectedYear]);

  const handlePNOAYearChange = (year: number) => setPNOASelectedYear(year);
  const handleMapDetailChange = (newMapDetail: Array<string>) => newMapDetail !== mapDetail && setMapDetail(newMapDetail);
  const handleOverLayerChange = (newOverLayerIds: Array<string>) => newOverLayerIds !== overlayIds && setOverlayIds(newOverLayerIds);

  //FILTERS-------------------------------------------------------------------------------------------------------------
  const handleFilterChange = (newFilters: Partial<FilterGroup>) => setFilterGroup(newFilters);

  const filteredBbox = filteredPrecincts?.bbox ?? null;

  const [sidePanelContentId, setSidePanelContentId] = useState<string>(SIDEPANEL_CONTENT_ID.FILTERS);
  const [mainContentId, setMainContentId] = useState<string>(MAIN_CONTENT_ID.MAP);

  const handleSidePanelContentChange = (actionId: string) => {
    if(mainContentId === MAIN_CONTENT_ID.DASHBOARD && actionId === SIDEPANEL_CONTENT_ID.LAYERS) {
      setMainContentId(MAIN_CONTENT_ID.MAP);
    }
    setSidePanelContentId(actionId);
  };

  const handleContentActionIdChange = () =>
    setMainContentId(mainContentId === MAIN_CONTENT_ID.MAP ? MAIN_CONTENT_ID.DASHBOARD : MAIN_CONTENT_ID.MAP);

  useEffect(() => {
    if(mainContentId === MAIN_CONTENT_ID.DASHBOARD && sidePanelContentId === SIDEPANEL_CONTENT_ID.LAYERS) {
      setSidePanelContentId(SIDEPANEL_CONTENT_ID.FILTERS);
    }
  }, [mainContentId, sidePanelContentId]);

  //OWNER
  const owner = otherUsers?.find(u => u.uuid === selectedParcel?.userUuid);

  //LOADING
  const isLoading = manifestLoading || profileLoading || otherUsersLoading || filteredPrecinctsLoading || dashboardLoading || selectedPrecinctLoading || selectedParcelLoading;

  const sidePanelContent = <SidePanelContent
    owner={owner}
    mapDetail={mapDetail}
    mapStyleId={mapStyleId}
    PNOASelectedYear={PNOASelectedYear}
    onMapDetailChange={handleMapDetailChange}
    onPNOAYearChange={handlePNOAYearChange}
    onStyleChange={handleMapStyleChange}
    manifest={manifest}
    selectedPrecinct={selectedPrecinct}
    selectedParcel={selectedParcel}
    overlayIds={overlayIds}
    profile={profile}
    sidePanelContentId={sidePanelContentId}
    users={otherUsers}
    year={year}
    filterGroup={filterGroup}
    onYearChange={handleYearChange}
    onCropChange={handleInfoSave}
    onUseChange={handleInfoSave}
    onFilterChange={handleFilterChange}
    onOverlayChange={handleOverLayerChange}
  />;

  const mainContent = mainContentId === MAIN_CONTENT_ID.MAP
    ? <MainContent
      selectedPrecinctId={selectedPrecinct?.id}
      crops={manifest?.crops}
      uses={manifest?.uses}
      filters={filterGroup}
      filteredPrecinctBbox={filteredBbox}
      profile={profile}
      mapStyle={mapStyle}
      mapDetail={mapDetail}
      overlayIds={overlayIds}
      year={year}
      viewport={viewport}
      onPointSelected={handlePointSelected}
      onViewportChange={setViewport}
    />
    : dashboardData
      ? <DashboardView
        data={dashboardData}
        crops={manifest?.crops}
        uses={manifest?.uses}
      />
      : <Typography>{t( '(No hay datos)')}</Typography>;

  return <Layout
    isLoading={isLoading}
    sidePanelContentId={sidePanelContentId}
    mainContentId={mainContentId}
    mainContent={mainContent}
    profile={profile}
    sidePanelContent={sidePanelContent}
    onActionIdChange={handleSidePanelContentChange}
    onContentActionClick={handleContentActionIdChange}
  />;
};

export default Index;